import type {ReactNode} from 'react';
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from '@headlessui/react';
import {XMarkIcon} from '@heroicons/react/24/outline';

interface ModalProps {
    children: ReactNode;
    open: boolean;
    updateOpenState: (open: boolean) => void;
    title: ReactNode;
    width?: 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
    belowOverlays?: boolean;
}

const maxWidths: Record<NonNullable<ModalProps['width']>, string> = {
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
    '3xl': 'sm:max-w-3xl',
    '4xl': 'sm:max-w-4xl',
    '5xl': 'sm:max-w-5xl',
};

/**
 * Centered content overlay (dimmed backdrop) for focused tasks that are too heavy
 * for a drawer. Full-screen on mobile, centered card on larger screens; the header
 * stays fixed while the body scrolls.
 */
export default function Modal({children, open, updateOpenState, title, width = '3xl', belowOverlays = false}: ModalProps) {
    return (
        <Dialog open={open} onClose={updateOpenState} className={`relative ${belowOverlays ? 'z-40' : 'z-50'}`}>
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-stretch justify-center p-0 sm:items-center sm:p-4">
                    <DialogPanel
                        transition
                        className={`relative flex h-full max-h-full w-full ${maxWidths[width]} transform flex-col overflow-hidden bg-white shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:h-auto sm:max-h-[85vh] sm:rounded-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95`}
                    >
                        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-8 py-5">
                            <DialogTitle as="h2" className="text-lg font-semibold text-gray-900">
                                {title}
                            </DialogTitle>
                            <button
                                type="button"
                                onClick={() => updateOpenState(false)}
                                aria-label="Sluiten"
                                className="rounded-md text-gray-400 transition hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                            >
                                <XMarkIcon aria-hidden="true" className="h-6 w-6"/>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-8 py-8">
                            {children}
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
